import set from 'lodash/set'
import omit from 'lodash/omit'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import React from 'react'
import { AddressDisplayContent } from './AddressDisplayContent'
import { NumberInput, TextInput } from '@ztechapps/webui.input'
import type { SelectOption } from '@ztechapps/webui.input'
import { ShowIf } from '@ztechapps/webui.util'
import { CountrySelect } from '../CountrySelect'
import { UsStateSelect } from '../UsStateSelect'
import type { Address } from '@ztechapps/common.contract'
import { buildAddress } from '@ztechapps/common.utils'
import { Col, Form, InputGroup, Row } from 'react-bootstrap'
import { BusyButton } from '@ztechapps/webui.button'
import { useAddressByZip } from '@ztechapps/common.hook'
import merge from 'lodash/merge'

export type AddressWithError = Partial<Address> & {
  errors?: Record<string, { validationState: string; error: string; touched: boolean }>
}

export const AddressInput = ({
  value,
  onChange,
  includeGps,
  includeStreet3 = true,
  includeCompany,
  includeAttention,
  includePhone,
  disabled,
  submitting,
}: {
  value: AddressWithError
  onChange: (addressWithError: AddressWithError) => void
  includeGps?: boolean
  includeStreet3?: boolean
  includeCompany?: boolean
  disabled?: boolean
  submitting?: boolean
  includeAttention?: boolean
  includePhone?: boolean
}) => {
  const onChangeAddress = onChange
  const defaultAddress = merge(
    {
      country: 'USA',
      fullAddress: 'USA',
    },
    value
  )

  const setValue = (name: string) => {
    return {
      onChange: (e, actualValue?: any) => {
        let mergeAddress = {
          ...(actualValue ?? value),
        }

        //get the value of input, when it's a select it will fall back to the "e"
        let mergeValue = e?.target?.value ?? e

        //when the field name is country, it is a Select control so we need to get from the name
        if (name === 'country') {
          mergeValue = e?.value
          // keep State on a re-pick (react-select still fires onChange); an empty Country is USA, as defaulted below
          if ((mergeValue || 'USA') !== (mergeAddress.country || 'USA')) {
            mergeAddress.state = ''
          }
        }
        mergeAddress = set(mergeAddress, name, mergeValue)

        //clear the lat and lng if the address was edit
        if (name !== 'gps.lng' && name !== 'gps.lat') {
          mergeAddress = omit(mergeAddress, ['gps'])
        }

        //when there is no value for the country, make it default to USA
        if (isEmpty(mergeAddress?.country)) {
          mergeAddress.country = 'USA'
        }

        //let _address = Utils.buildAddress({ contact: { address1: mergeAddress } })
        // US state validity is enforced at the field itself (dropdown when USA, free text otherwise) — no inline validation here

        let company = mergeAddress?.company ?? '',
          attention = mergeAddress?.name ?? '',
          phone = mergeAddress?.phone ?? '',
          street3 = mergeAddress.street3 ?? ''
        //try to rebuild the address again using values from country
        mergeAddress = buildAddress({ contact: { address1: mergeAddress } })

        if (name === 'company') {
          company = e?.target?.value
        }
        mergeAddress.company = company

        if (name === 'street3') {
          street3 = e?.target?.value
        }
        mergeAddress.street3 = street3

        if (name === 'name') {
          attention = e?.target?.value
        }
        mergeAddress.name = attention

        if (name === 'phone') {
          phone = e?.target?.value
        }
        mergeAddress.phone = phone

        const { fullAddress } = buildAddress(mergeAddress)

        mergeAddress.fullAddress = fullAddress

        onChangeAddress?.({ ...mergeAddress, errors: null })
      },
      value: get(defaultAddress, name),
    }
  }

  //empty country defaults to USA (mirrors the country handling above), so it also gets the state dropdown
  const isUSA = isEmpty(value?.country) || value?.country === 'USA'
  const country = setValue('country')

  return (
    <Row className={'g-2'}>
      <ShowIf condition={includeCompany}>
        <Col md={12}>
          <AddressDisplayContent
            disabled={disabled || submitting}
            {...{
              ...setValue('company'),
              label: 'Company',
            }}
          />
        </Col>
      </ShowIf>
      <ShowIf condition={includeAttention}>
        <Col md={6}>
          <AddressDisplayContent
            disabled={disabled || submitting}
            {...{
              ...setValue('name'),
              label: 'Attention',
            }}
          />
        </Col>
      </ShowIf>
      <ShowIf condition={includePhone}>
        <Col md={6}>
          <AddressDisplayContent
            disabled={disabled || submitting}
            {...{
              ...setValue('phone'),
              label: 'Phone',
            }}
          />
        </Col>
      </ShowIf>
      <Col md={12}>
        <AddressDisplayContent
          disabled={disabled || submitting}
          {...{
            ...setValue('street1'),
            label: 'Address Line 1',
          }}
        />
      </Col>
      <Col md={12}>
        <AddressDisplayContent
          disabled={disabled || submitting}
          {...{
            ...setValue('street2'),
            label: 'Address Line 2',
          }}
        />
      </Col>
      <ShowIf condition={includeStreet3}>
        <Col md={12}>
          <AddressDisplayContent
            disabled={disabled || submitting}
            {...{
              ...setValue('street3'),
              label: 'Address Line 3',
            }}
          />
        </Col>
      </ShowIf>
      <Col md={12} lg={6}>
        <AddressDisplayContent
          disabled={disabled || submitting}
          {...{
            ...setValue('city'),
            label: 'City/District',
          }}
        />
      </Col>
      <Col md={12} lg={6}>
        <AddressDisplayContent
          disabled={disabled || submitting}
          label="State/Province/Region"
          {...(isUSA
            ? {
                content: (
                  <UsStateSelect
                    disabled={disabled || submitting}
                    value={setValue('state').value}
                    onChange={(option) => setValue('state').onChange((option as SelectOption)?.value ?? '', value)}
                  />
                ),
              }
            : {
                ...setValue('state'),
                value: value?.state,
              })}
        />
      </Col>
      <Col md={12} lg={6}>
        <ZipAddressDisplayContent setValue={setValue} disabled={disabled || submitting} address={value} />
      </Col>

      <Col md={12} lg={6}>
        <AddressDisplayContent
          {...{
            label: 'Country',
            // react-select's second `onChange` argument is its action metadata, never an address, so the current address is passed as the base.
            content: (
              <CountrySelect
                disabled={disabled || submitting}
                value={country.value}
                onChange={(option) => country.onChange(option, value)}
              />
            ),
          }}
        />
      </Col>

      <ShowIf condition={includeGps}>
        <Col md={12}>
          <Row>
            <Col md={6}>
              <AddressDisplayContent
                {...{
                  label: 'Longitude',
                  content: <NumberInput disabled={disabled || submitting} {...setValue('gps.lng')} step={0.00001} />,
                }}
              />
            </Col>
            <Col md={6}>
              <AddressDisplayContent
                {...{
                  label: 'Latitude',
                  content: <NumberInput disabled={disabled || submitting} {...setValue('gps.lat')} step={0.00001} />,
                }}
              />
            </Col>
          </Row>
        </Col>
      </ShowIf>
    </Row>
  )
}

const ZipAddressDisplayContent = ({
  disabled,
  setValue,
  address,
}: {
  disabled: boolean
  address: AddressWithError
  setValue: (name: string) => any
}) => {
  //'http://localhost:3000/address/zip' for local address list
  const [fetchAddress, { loading, error }] = useAddressByZip()

  const value = setValue('zip').value
  const country = setValue('country').value
  const isUSA = country === 'USA'

  const onSubmit = async (e) => {
    e.preventDefault()

    if (!value || !isUSA) {
      return
    }

    const { state, city } = (await fetchAddress(value)) ?? {}
    const newAddress = { ...address, state, city }

    setValue('city').onChange(city, newAddress)
    setValue('state').onChange(state, newAddress)
  }

  const disabledInput = disabled || loading
  const disabledSearch = disabledInput || isEmpty(value?.trim())

  const onChange = (e) => {
    setValue('zip').onChange(e, null)
  }

  let errorText = ''
  if (error && isUSA && value) {
    errorText = `If you think this ZIP code is valid, just ignore this message and continue.`
  }

  return (
    <AddressDisplayContent
      meta={{
        error: errorText,
        validationState: 'error',
        touched: true,
      }}
      {...{
        label: 'ZIP/Postal Code',
        content: (
          <form onSubmit={onSubmit}>
            <Form.Group>
              <InputGroup>
                <TextInput disabled={disabledInput} {...setValue('zip')} onChange={onChange} />
                <ShowIf condition={isUSA}>
                  <BusyButton icon="search" type="submit" disabled={disabledSearch} isBusy={loading} busyText="" />
                </ShowIf>
              </InputGroup>
            </Form.Group>
          </form>
        ),
      }}
    />
  )
}
